import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { createFirestoreApi } from '#firebase/firestoreApi'
import stripQuestReferencesToSubmission from '#quests/stripQuestReferencesToSubmission'
import { useShowSurveyQuery } from '#survey/api'

import { type SubmissionDoc, submissionSchema } from '.'

const api = createFirestoreApi({
  authenticated: true,
  name: 'submissions',
  schema: submissionSchema,
})

export const {
  useListQuery: useListSubmissionsQuery,
  useShowQuery: useShowSubmissionQuery,
  useCreateMutation: useCreateSubmissionMutation,
  useUpdateMutation: useUpdateSubmissionMutation,
  useUpsertMutation: useUpsertSubmissionMutation,
} = api

const { useDeleteMutation: useDeleteSubmissionMutationInternal } = api

// Wraps the factory-generated delete mutation to also strip any tuning entries
// in quests that were targeting this submission's answers.
export const useDeleteSubmissionMutation = (): UseMutationResult<
  void,
  Error,
  string
> => {
  const qc = useQueryClient()
  const inner = useDeleteSubmissionMutationInternal()
  return useMutation({
    mutationFn: async (id: string) => {
      await inner.mutateAsync(id)
      await stripQuestReferencesToSubmission(id)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quests'] }),
  })
}

export const useListSurveySubmissionsQuery = (
  surveyId: string
): UseQueryResult<SubmissionDoc[], Error> => {
  const showSurvey = useShowSurveyQuery(surveyId)

  const topicIds = useMemo(
    () => showSurvey.data?.topics.map(({ id }) => id) ?? [],
    [showSurvey.data]
  )
  const filter = useCallback(
    (submissionDoc: SubmissionDoc) =>
      topicIds.includes(submissionDoc.entity.topicId),
    [topicIds]
  )

  return useListSubmissionsQuery({ filter })
}
